"use client";

import { cn } from "@/lib/utils";
import type React from "react";
import { useMemo, memo } from "react";
import type { ReactNode } from "react";
type AreaSize = "25%" | "50%" | "75%" | "100%";
type Placement = "top-left" | "top-center" | "top-right";
type Direction = "default" | "straight" | "reversed";
type AnimationSpeed = "fast" | "medium" | "slow";
type DotSpacing = "tight" | "normal" | "loose" | "wide";
type Intensity = "low" | "medium" | "high" | "ultra";
type Theme = "default" | "dusk" | "warm" | "oceanic" | "sunset" | "neon" | "midnight" | "forest" | "lavender" | "cosmic";

// Custom CSS Properties interface to handle the aurora custom properties
interface CustomCSSProperties extends React.CSSProperties {
  "--aurora"?: string;
  "--dark-gradient"?: string;
  "--white-gradient"?: string;
  "--primary"?: string;
  "--secondary"?: string;
  "--tertiary"?: string;
  "--quaternary"?: string;
  "--quinary"?: string;
  "--black"?: string;
  "--white"?: string;
  "--transparent"?: string;
  "--animation-duration"?: string;
  "--animation-function"?: string;
  "--aurora-opacity"?: string;
  "--aurora-blur"?: string;
  "--aurora-scale"?: string;
  "--dynamic-fade-radius"?: string;
  "--hover-fade-radius"?: string;
  "--area-mask-default"?: string;
  "--area-mask-hover"?: string;
}
interface AuroraBackgroundProps extends React.HTMLProps<HTMLDivElement> {
  children: ReactNode;
  showRadialGradient?: boolean;
  areaSize?: AreaSize;
  placement?: Placement;
  direction?: Direction;
  animationSpeed?: AnimationSpeed;
  theme?: Theme;
  opacity?: number;
  auroraClassName?: string;
  rayClassName?: string;
  intensity?: Intensity;

  // Dynamic area size controls
  hoverAreaSize?: AreaSize;
  enableDynamicAreaSize?: boolean;

  // Dotted background props
  enableDottedBackground?: boolean;
  dotSpacing?: DotSpacing | number;
  dotSize?: number;
  dotOpacity?: number;
  dotBackgroundOpacity?: number;
  enableVignette?: boolean;
  vignetteOpacity?: number;

  // Animation control
  disableAnimations?: boolean;
}

// Theme color palettes
const THEME_COLORS = {
  default: {
    primary: "#3b82f6",
    // blue-500
    secondary: "#a5b4fc",
    // indigo-300
    tertiary: "#93c5fd",
    // blue-300
    quaternary: "#ddd6fe",
    // violet-200
    quinary: "#60a5fa" // blue-400
  },
  dusk: {
    primary: "#7e22ce",
    // purple-700
    secondary: "#c4b5fd",
    // violet-300
    tertiary: "#a78bfa",
    // violet-400
    quaternary: "#f5d0fe",
    // fuchsia-200
    quinary: "#8b5cf6" // violet-500
  },
  warm: {
    primary: "#f59e0b",
    // amber-500
    secondary: "#fcd34d",
    // amber-300
    tertiary: "#fbbf24",
    // amber-400
    quaternary: "#fb923c",
    // orange-400
    quinary: "#ef4444" // red-500
  },
  oceanic: {
    primary: "#0891b2",
    // cyan-600
    secondary: "#22d3ee",
    // cyan-400
    tertiary: "#06b6d4",
    // cyan-500
    quaternary: "#67e8f9",
    // cyan-300
    quinary: "#0284c7" // sky-600
  },
  sunset: {
    primary: "#f97316",
    // orange-500
    secondary: "#c2410c",
    // orange-700
    tertiary: "#fb923c",
    // orange-400
    quaternary: "#fca5a5",
    // red-300
    quinary: "#ef4444" // red-500
  },
  neon: {
    primary: "#059669",
    // emerald-600
    secondary: "#22d3ee",
    // cyan-400
    tertiary: "#a3e635",
    // lime-400
    quaternary: "#fcd34d",
    // amber-300
    quinary: "#4f46e5" // indigo-600
  },
  // New themes with strong contrast to sunset
  midnight: {
    primary: "#1e40af",
    // blue-800
    secondary: "#1d4ed8",
    // blue-700
    tertiary: "#3b82f6",
    // blue-500
    quaternary: "#0f172a",
    // slate-900
    quinary: "#6366f1" // indigo-500
  },
  forest: {
    primary: "#15803d",
    // green-700
    secondary: "#16a34a",
    // green-600
    tertiary: "#22c55e",
    // green-500
    quaternary: "#4ade80",
    // green-400
    quinary: "#065f46" // emerald-800
  },
  lavender: {
    primary: "#7c3aed",
    // violet-600
    secondary: "#8b5cf6",
    // violet-500
    tertiary: "#a78bfa",
    // violet-400
    quaternary: "#c4b5fd",
    // violet-300
    quinary: "#6d28d9" // violet-700
  },
  cosmic: {
    primary: "#312e81",
    // indigo-900
    secondary: "#4338ca",
    // indigo-700
    tertiary: "#818cf8",
    // indigo-400
    quaternary: "#c7d2fe",
    // indigo-200
    quinary: "#6d28d9" // violet-700
  }
};

// Default colors
const BASE_COLORS = {
  black: "#000",
  white: "#fff",
  transparent: "transparent"
};

// Animation durations mapped by speed option
const ANIMATION_DURATIONS = {
  slow: "30s",
  medium: "20s",
  fast: "10s"
};

// Animation timing functions mapped by speed option for smoother animations
const ANIMATION_TIMING_FUNCTIONS = {
  slow: "cubic-bezier(0.4, 0.0, 0.2, 1)",
  // More steps for slow animations
  medium: "cubic-bezier(0.4, 0.0, 0.2, 1)",
  // Smooth easing for medium
  fast: "linear" // Linear is fine for fast as it's already smooth
};

// Dot spacing presets
const DOT_SPACINGS = {
  tight: 8,
  normal: 12,
  loose: 16,
  wide: 24
};

// Intensity configurations similar to gradient border
const INTENSITY_CONFIG = {
  low: {
    opacity: 0.6,
    blur: "8px",
    scale: 0.8
  },
  medium: {
    opacity: 0.8,
    blur: "10px",
    scale: 1
  },
  high: {
    opacity: 1,
    blur: "10px",
    scale: 1
  },
  ultra: {
    opacity: 1.2,
    blur: "12px",
    scale: 1.1
  }
};

// Function to generate gradient strings based on direction and colors
const generateGradients = (direction: string, colors: typeof THEME_COLORS.default) => {
  const {
    primary,
    secondary,
    tertiary,
    quaternary,
    quinary
  } = colors;
  const directionAngles = {
    straight: "180deg",
    reversed: "80deg",
    default: "100deg"
  };
  const angle = directionAngles[direction as keyof typeof directionAngles] || directionAngles.default;
  return {
    aurora: `repeating-linear-gradient(${angle},var(--primary) 10%,var(--secondary) 15%,var(--tertiary) 20%,var(--quaternary) 25%,var(--quinary) 30%)`,
    dark: `repeating-linear-gradient(${angle},var(--black) 0%,var(--black) 7%,var(--transparent) 10%,var(--transparent) 12%,var(--black) 16%)`,
    white: `repeating-linear-gradient(${angle},var(--white) 0%,var(--white) 7%,var(--transparent) 10%,var(--transparent) 12%,var(--white) 16%)`
  };
};

// Fade radius values by area size
const FADE_RADIUS = {
  "25%": "30%",
  "50%": "60%",
  "75%": "90%",
  "100%": "60%" // Default
};

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, opacity: number) => {
  const cleanHex = hex.replace(/^#/, "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Create base component to be wrapped with memo
const AuroraBackgroundBase = ({
  className,
  children,
  showRadialGradient = true,
  areaSize = "25%",
  placement = "top-right",
  direction = "default",
  animationSpeed = "medium",
  theme = "sunset",
  opacity = 0.5,
  // Default opacity at 50%
  auroraClassName,
  rayClassName,
  intensity = "medium",
  // Dynamic area size controls
  hoverAreaSize,
  enableDynamicAreaSize,
  // Dotted background props
  enableDottedBackground = false,
  dotSpacing = "normal",
  dotSize = 1,
  dotOpacity = 0.6,
  dotBackgroundOpacity = 0.1,
  enableVignette = true,
  vignetteOpacity = 0.3,
  // Animation control
  disableAnimations = false,
  ...props
}: AuroraBackgroundProps) => {
  // Ensure opacity is within valid range
  const safeOpacity = useMemo(() => {
    return Math.max(0, Math.min(1, opacity));
  }, [opacity]);

  // Calculate placement styles based on area size and placement
  const placementStyles = useMemo(() => {
    const styles: React.CSSProperties = {};

    // Use dynamic area size if enabled, otherwise use static areaSize
    const currentAreaSize = enableDynamicAreaSize ? "var(--dynamic-area-size, 25%)" : areaSize;
    if (currentAreaSize !== "100%" && !enableDynamicAreaSize) {
      // Get fade radius from cache for static area sizes
      const fadeRadius = FADE_RADIUS[areaSize as keyof typeof FADE_RADIUS];

      // Create mask gradients based on placement
      let maskImage = "";
      switch (placement) {
        case "top-left":
          maskImage = `radial-gradient(circle at 0% 0%, black 5%, transparent ${fadeRadius})`;
          break;
        case "top-right":
          maskImage = `radial-gradient(circle at 100% 0%, black 5%, transparent ${fadeRadius})`;
          break;
        case "top-center":
        default:
          maskImage = `radial-gradient(circle at 50% 0%, black 5%, transparent ${fadeRadius})`;
          break;
      }
      styles.maskImage = maskImage;
      styles.WebkitMaskImage = maskImage;
    } else if (enableDynamicAreaSize) {
      // Dynamic area size using CSS custom properties
      let maskImage = "";
      const fadeRadius = "var(--dynamic-fade-radius, 30%)";
      switch (placement) {
        case "top-left":
          maskImage = `radial-gradient(circle at 0% 0%, black 5%, transparent ${fadeRadius})`;
          break;
        case "top-right":
          maskImage = `radial-gradient(circle at 100% 0%, black 5%, transparent ${fadeRadius})`;
          break;
        case "top-center":
        default:
          maskImage = `radial-gradient(circle at 50% 0%, black 5%, transparent ${fadeRadius})`;
          break;
      }
      styles.maskImage = maskImage;
      styles.WebkitMaskImage = maskImage;
    }
    return styles;
  }, [areaSize, placement, enableDynamicAreaSize]);

  // Get theme colors
  const themeColors = useMemo(() => {
    return THEME_COLORS[theme] || THEME_COLORS.default;
  }, [theme]);

  // Generate gradients based on direction and theme
  const gradients = useMemo(() => {
    return generateGradients(direction, themeColors);
  }, [direction, themeColors]);

  // Calculate dot spacing value
  const spacing = useMemo(() => {
    return typeof dotSpacing === "number" ? dotSpacing : DOT_SPACINGS[dotSpacing];
  }, [dotSpacing]);

  // Get gradient styles using theme colors and generated gradients
  const gradientStyles = useMemo(() => {
    const duration = ANIMATION_DURATIONS[animationSpeed] || ANIMATION_DURATIONS.medium;
    const timingFunction = ANIMATION_TIMING_FUNCTIONS[animationSpeed] || ANIMATION_TIMING_FUNCTIONS.medium;
    const intensityConfig = INTENSITY_CONFIG[intensity];

    // Calculate dynamic area size variables
    const defaultFadeRadius = FADE_RADIUS[areaSize as keyof typeof FADE_RADIUS] || "30%";
    const hoverFadeRadius = hoverAreaSize ? FADE_RADIUS[hoverAreaSize as keyof typeof FADE_RADIUS] : "60%";

    // Generate complete mask gradients for animation
    let defaultMask = "";
    let hoverMask = "";
    if (enableDynamicAreaSize) {
      switch (placement) {
        case "top-left":
          defaultMask = `radial-gradient(circle at 0% 0%, black 5%, transparent ${defaultFadeRadius})`;
          hoverMask = `radial-gradient(circle at 0% 0%, black 5%, transparent ${hoverFadeRadius})`;
          break;
        case "top-right":
          defaultMask = `radial-gradient(circle at 100% 0%, black 5%, transparent ${defaultFadeRadius})`;
          hoverMask = `radial-gradient(circle at 100% 0%, black 5%, transparent ${hoverFadeRadius})`;
          break;
        case "top-center":
        default:
          defaultMask = `radial-gradient(circle at 50% 0%, black 5%, transparent ${defaultFadeRadius})`;
          hoverMask = `radial-gradient(circle at 50% 0%, black 5%, transparent ${hoverFadeRadius})`;
          break;
      }
    }
    return {
      "--primary": themeColors.primary,
      "--secondary": themeColors.secondary,
      "--tertiary": themeColors.tertiary,
      "--quaternary": themeColors.quaternary,
      "--quinary": themeColors.quinary,
      "--black": BASE_COLORS.black,
      "--white": BASE_COLORS.white,
      "--transparent": BASE_COLORS.transparent,
      "--animation-duration": duration,
      "--animation-function": timingFunction,
      "--aurora": gradients.aurora,
      "--dark-gradient": gradients.dark,
      "--white-gradient": gradients.white,
      "--aurora-opacity": (safeOpacity * intensityConfig.opacity).toString(),
      "--aurora-blur": intensityConfig.blur,
      "--aurora-scale": intensityConfig.scale.toString(),
      // Dynamic area size mask variables for animations
      "--area-mask-default": defaultMask,
      "--area-mask-hover": hoverMask
    } as CustomCSSProperties;
  }, [themeColors, gradients, animationSpeed, safeOpacity, intensity, areaSize, hoverAreaSize, enableDynamicAreaSize, placement]);

  // Cache the radial gradient class
  const radialGradientClass = useMemo(() => {
    return areaSize === "100%" && showRadialGradient ? `[mask-image:radial-gradient(ellipse_at_100%_0%,black_10%,var(--transparent)_70%)]` : "";
  }, [areaSize, showRadialGradient]);

  // Cache the aurora element className with dynamic opacity and external classNames for hover control
  const computedAuroraClassName = useMemo(() => {
    const baseClasses = `aurora-element backface-hidden pointer-events-none absolute -inset-[10px] transform-gpu opacity-[var(--aurora-opacity)] will-change-transform will-change-opacity [background-image:var(--dark-gradient),var(--aurora)] [background-position:50%_50%,50%_50%] [background-size:300%,_200%] [perspective:1000px] [transform-style:preserve-3d] after:absolute after:inset-0 after:mix-blend-difference after:content-[""] after:[background-attachment:fixed] after:[background-image:var(--dark-gradient),var(--aurora)] after:[background-size:200%,_100%] after:will-change-transform`;
    const animationClasses = disableAnimations ? "" : "after:[animation:aurora_var(--animation-duration)_var(--animation-function)_infinite] transition-opacity duration-700";
    return cn(baseClasses, animationClasses, `[filter:blur(var(--aurora-blur))_scale(var(--aurora-scale))]`, radialGradientClass, auroraClassName);
  }, [radialGradientClass, auroraClassName, disableAnimations]);

  // CSS-based dotted background component
  const DottedBackgroundLayer = useMemo(() => {
    if (!enableDottedBackground) return null;

    // Create background gradient similar to gradient border - transparent center, opaque edges
    const bgColor = hexToRgba("#000000", 1 - dotBackgroundOpacity); // Use black with inverse opacity
    const backgroundGradient = `radial-gradient(circle at 50% 50%, transparent 0%, transparent 20%, ${bgColor} 50%)`;
    const vignetteGradient = enableVignette ? `radial-gradient(circle at 50% 50%, transparent 30%, rgba(0,0,0,${vignetteOpacity}) 100%)` : "transparent";
    return <div className="pointer-events-none absolute inset-0 z-10 h-full w-full overflow-hidden">
        {/* Backdrop with dotted pattern - exactly like gradient border */}
        <div className="absolute inset-[-50%]" style={{
        background: backgroundGradient,
        backgroundSize: `${spacing}px ${spacing}px`
      }} />

        {/* Vignette overlay */}
        {enableVignette && <div className="absolute inset-0" style={{
        background: vignetteGradient
      }} />}
      </div>;
  }, [enableDottedBackground, enableVignette, dotBackgroundOpacity, vignetteOpacity, spacing]);
  return <main data-sentry-component="AuroraBackgroundBase" data-sentry-source-file="aurora-background.tsx">
      <div className={cn("transition-bg relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-3xl bg-zinc-50 text-slate-950 dark:bg-zinc-950",
    // Add dynamic area size support
    enableDynamicAreaSize && !disableAnimations && "aurora-dynamic-area group", className)} {...props}>
        {/* Dotted background layer */}
        {DottedBackgroundLayer}

        {/* Add CSS containment for better isolation and performance */}
        <div className={cn("absolute inset-0 z-0 overflow-hidden contain-paint", rayClassName)} style={gradientStyles}>
          <div className={computedAuroraClassName} style={placementStyles}></div>
        </div>
        {children}
      </div>

      {/* Add the keyframe animations and hover styles */}
      {!disableAnimations && <style jsx global>{`
          @keyframes aurora {
            0% {
              background-position:
                50% 50%,
                50% 50%;
              transform: translateZ(0);
            }
            20% {
              background-position:
                30% 70%,
                20% 20%;
              transform: translateZ(0);
            }
            40% {
              background-position:
                10% 90%,
                10% 10%;
              transform: translateZ(0);
            }
            50% {
              background-position:
                0% 100%,
                0% 0%;
              transform: translateZ(0);
            }
            60% {
              background-position:
                10% 90%,
                10% 10%;
              transform: translateZ(0);
            }
            80% {
              background-position:
                30% 70%,
                20% 20%;
              transform: translateZ(0);
            }
            100% {
              background-position:
                50% 50%,
                50% 50%;
              transform: translateZ(0);
            }
          }

          /* Dynamic area size hover support with animations */
          .aurora-dynamic-area .aurora-element {
            animation: area-size-out 500ms ease-in-out forwards;
          }

          .aurora-dynamic-area:hover .aurora-element {
            animation: area-size-in 500ms ease-in-out forwards;
          }

          @keyframes area-size-in {
            from {
              mask-image: var(--area-mask-default);
              -webkit-mask-image: var(--area-mask-default);
            }
            to {
              mask-image: var(--area-mask-hover);
              -webkit-mask-image: var(--area-mask-hover);
            }
          }

          @keyframes area-size-out {
            from {
              mask-image: var(--area-mask-hover);
              -webkit-mask-image: var(--area-mask-hover);
            }
            to {
              mask-image: var(--area-mask-default);
              -webkit-mask-image: var(--area-mask-default);
            }
          }
        `}</style>}
    </main>;
};

// Wrap component with memo to prevent unnecessary re-renders
export const AuroraBackground = memo(AuroraBackgroundBase);